<template>
  <div class="multi-upload-list">
    <!-- ファイル投入: ドロップゾーン + フォルダ選択 -->
    <div
      class="upload-dropzone mb-2"
      :class="{ 'is-dragover': dragActive, 'disabled': busy }"
      role="button"
      tabindex="0"
      @click="onDropzoneClick"
      @keydown.enter.prevent="openFileDialog"
      @keydown.space.prevent="openFileDialog"
      @dragenter.prevent.stop="onDragActive(true)"
      @dragover.prevent.stop="onDragActive(true)"
      @dragleave.prevent.stop="onDragActive(false)"
      @dragend.prevent.stop="onDragActive(false)"
      @drop.prevent.stop="onDrop"
    >
      <div class="upload-dropzone-inner text-center py-4 px-3">
        <i class="fa-solid fa-cloud-arrow-up fa-2x text-muted mb-2"></i>
        <p class="mb-2 text-muted">{{ __('video.upload_dropzone_hint') }}</p>
        <button type="button" class="btn btn-outline-primary btn-sm" :disabled="busy" @click.stop="openFileDialog">
          <i class="fa-solid fa-folder-open mr-1"></i>{{ __('video.upload_file_select_button') }}
        </button>
      </div>
    </div>
    <input
      ref="fileInput"
      type="file"
      multiple
      class="d-none"
      data-testid="upload-attachments-input"
      :accept="acceptAttribute"
      @change="onFileInputChange"
    />

    <div v-if="extensionNotice" class="alert alert-danger small py-2 px-3 mt-2 mb-0">
      <i class="fa-solid fa-circle-xmark mr-1"></i>{{ extensionNotice }}
    </div>
    <div v-if="duplicateNotice" class="alert alert-warning small py-2 px-3 mt-2 mb-0">
      <i class="fa-solid fa-triangle-exclamation mr-1"></i>{{ duplicateNotice }}
    </div>

    <!-- ファイルカード一覧 -->
    <div v-for="(item, idx) in items" :key="item._uuid" class="multi-upload-item card mb-2 shadow-sm">
      <div class="card-body d-flex align-items-center py-2 px-3 flex-wrap">
        <span class="badge badge-secondary mr-2">#{{ idx + 1 }}</span>
        <i class="fa-regular fa-file mr-1 text-muted"></i>
        <span class="font-weight-bold">{{ item.original_name }}</span>
        <span class="text-muted small ml-2">{{ formatSize(item.filesize) }}</span>

        <span class="small ml-3" :class="statusClass(item.status)">
          <i :class="statusIcon(item.status)" class="mr-1"></i>{{ statusLabel(item.status) }}
          <template v-if="item.status === 'uploading'"> {{ item.percent }}%</template>
        </span>

        <button
          v-if="item.status === 'pending' && !busy"
          type="button"
          class="btn btn-link text-danger p-0 ml-auto"
          @click="removeItem(idx)"
        >
          <i class="fa-solid fa-trash-can"></i>
        </button>
      </div>

      <div v-if="item.status === 'uploading' || (item.status === 'failed' && item.error)" class="px-3 pb-2">
        <div v-if="item.status === 'uploading'" class="progress" style="height: 6px;">
          <div
            class="progress-bar"
            role="progressbar"
            :style="{ width: item.percent + '%' }"
            :aria-valuenow="item.percent"
            aria-valuemin="0"
            aria-valuemax="100"
          ></div>
        </div>
        <div v-if="item.status === 'failed' && item.error" class="text-danger small">{{ item.error }}</div>
      </div>

      <input type="hidden" :name="prefix + '_' + idx + '_filename'" :value="item.filename || ''" />
      <input type="hidden" :name="prefix + '_' + idx + '_filesize'" :value="item.filesize || 0" />
      <input type="hidden" :name="prefix + '_' + idx + '_original_name'" :value="item.original_name || ''" />
      <input type="hidden" :name="prefix + '_' + idx + '_extension'" :value="item.extension || ''" />
      <!-- 登録時にサーバが発行したキーかを確かめるため、check-upload の期限と署名を送る -->
      <input type="hidden" :name="prefix + '_' + idx + '_upload_expires_at'" :value="item.upload_expires_at || ''" />
      <input type="hidden" :name="prefix + '_' + idx + '_upload_token'" :value="item.upload_token || ''" />
    </div>

    <!-- アップロード操作 -->
    <div v-if="items.length > 0" class="mt-2">
      <button
        type="button"
        class="btn btn-primary btn-sm"
        :disabled="busy || !hasPending"
        @click="uploadAll('pending')"
      >
        <i class="fa-solid fa-upload mr-1"></i>{{ __('video.upload_action_button') }}
      </button>
      <button
        v-if="hasFailed && !busy"
        type="button"
        class="btn btn-warning btn-sm ml-2"
        @click="uploadAll('failed')"
      >
        <i class="fa-solid fa-rotate-right mr-1"></i>{{ __('video.upload_retry_failed') }}
      </button>
      <span v-if="uploadProgressText" class="ml-2 small text-muted">{{ uploadProgressText }}</span>
      <span v-if="uploadCompletionText" class="ml-2 text-danger small font-weight-bold">
        {{ uploadCompletionText }}
      </span>
      <span v-if="allUploaded" class="ml-2 text-success small font-weight-bold">
        <i class="fa-solid fa-circle-check mr-1"></i>{{ __('video.upload_all_done') }}
      </span>
    </div>
  </div>
</template>

<script>
import { uploadWithPresignedUrls } from '../lib/presignedUpload';

// 添付ファイルとして許可する拡張子（画面表記の「※ pdf, txt, doc, ...」と一致させる）
const ALLOWED_ATTACHMENT_EXTENSIONS = ['pdf', 'txt', 'doc', 'docx', 'xls', 'xlsx', 'ppt', 'pptx'];

// S3 への同時アップロード件数 (2026-09-28 の work 実測では、同時 3 件でも所要時間が
// ほぼ変わらなかった (ブラウザのメインスレッドの処理待ちが律速だった)。そのため、
// 利用者環境で効果を確認できるまでは 1 (直列) にしている。値を増やせば並列で送る。
// (hover: none) 端末は 1 件ずつの直列を維持する。
// 2026-10-11: PC は 8 件まで同時に送る (デプロイからテスト完了までの短縮。E2E の P16 は 8 ファイルを直列に送って
// 12.3 秒かかっていた)。
const UPLOAD_CONCURRENCY_PC = 8;
const UPLOAD_CONCURRENCY_MOBILE = 1;

export default {
  name: 'MultipleUpload',
  emits: ['uploading', 'count-changed'],
  props: {
    // 署名 API の URL 一式 (WebUploadScope::endpoints)。S3 へはこの API が出す署名付き URL で上げる
    presign_endpoints: { type: Object, default: () => ({}) },
    prefix: { type: String, default: 'attachments' },
    check_upload_endpoint: { type: String, default: '' },
  },
  data() {
    return {
      items: [],
      busy: false,
      uploadSessionTotal: 0,
      uploadSessionDone: 0,
      uploadSessionFailed: 0,
      dragActive: false,
      duplicateNotice: '',
      extensionNotice: '',
    };
  },
  computed: {
    acceptAttribute() {
      return ALLOWED_ATTACHMENT_EXTENSIONS.map((e) => '.' + e).join(',');
    },
    hasPending() {
      return this.items.some((it) => it.status === 'pending');
    },
    hasFailed() {
      return this.items.some((it) => it.status === 'failed');
    },
    allUploaded() {
      return this.items.length > 0 && this.items.every((it) => it.status === 'success');
    },
    itemCount() {
      return this.items.length;
    },
    isMobile() {
      return window.matchMedia('(hover: none)').matches;
    },
    uploadConcurrency() {
      return this.isMobile ? UPLOAD_CONCURRENCY_MOBILE : UPLOAD_CONCURRENCY_PC;
    },
    uploadProgressText() {
      if (!this.busy || this.uploadSessionTotal === 0) return '';
      let text = this.__('video.upload_progress_uploading')
        .replace(':done', String(this.uploadSessionDone))
        .replace(':total', String(this.uploadSessionTotal));
      if (this.uploadSessionFailed > 0) {
        text += this.__('video.upload_progress_failed_suffix')
          .replace(':count', String(this.uploadSessionFailed));
      }
      return text;
    },
    uploadCompletionText() {
      if (this.busy) return '';
      const failedCount = this.items.filter((it) => it.status === 'failed').length;
      if (failedCount === 0) return '';
      const successCount = this.items.filter((it) => it.status === 'success').length;
      return this.__('video.upload_progress_done')
        .replace(':done', String(successCount))
        .replace(':count', String(failedCount));
    },
  },
  watch: {
    items: {
      deep: true,
      handler() {
        // 添付は任意。未アップロードの添付が残っている間だけ「登録」を抑止する
        this.$emit('uploading', this.items.some((it) => it.status !== 'success'));
      },
    },
    // 添付ファイルの件数を親へ通知する（親側は「添付先ファイル」セレクトの表示可否に利用）
    itemCount: {
      immediate: true,
      handler(count) {
        this.$emit('count-changed', count);
      },
    },
  },
  methods: {
    generateUuid() {
      if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
        return crypto.randomUUID();
      }
      return 'id-' + Math.random().toString(36).slice(2) + Date.now();
    },
    extractExtension(name) {
      const m = (name || '').match(/(.*)(?:\.([^.]+$))/);
      return m ? m[2] : '';
    },
    normalizeFileName(value) {
      if (!value) return '';
      const raw = value.toString().trim().replace(/\\/g, '/');
      const base = (raw.split('/').pop() || '').trim().toLowerCase();
      // macOS 由来のファイル名は濁点等が NFD で保存される。NFC に揃えて比較する
      return base.normalize ? base.normalize('NFC') : base;
    },
    formatSize(bytes) {
      const n = Number(bytes) || 0;
      if (n < 1024) return `${n} B`;
      if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
      if (n < 1024 * 1024 * 1024) return `${(n / 1024 / 1024).toFixed(1)} MB`;
      return `${(n / 1024 / 1024 / 1024).toFixed(2)} GB`;
    },
    statusLabel(status) {
      const map = {
        pending: this.__('video.upload_status_pending'),
        uploading: this.__('video.upload_status_uploading'),
        success: this.__('video.upload_status_success'),
        failed: this.__('video.upload_status_failed'),
      };
      return map[status] || '';
    },
    statusIcon(status) {
      const map = {
        pending: 'fa-regular fa-clock',
        uploading: 'fa-solid fa-spinner fa-spin',
        success: 'fa-solid fa-circle-check',
        failed: 'fa-solid fa-circle-xmark',
      };
      return map[status] || '';
    },
    statusClass(status) {
      const map = {
        pending: 'text-muted',
        uploading: 'text-primary',
        success: 'text-success',
        failed: 'text-danger',
      };
      return map[status] || '';
    },
    existingAttachmentNames() {
      // 編集画面で既に登録済みの添付ファイル名（重複判定用）
      const el = document.getElementById('attachments_existing_names');
      if (!el || !el.value) return [];
      try {
        return JSON.parse(el.value).map((n) => this.normalizeFileName(n)).filter(Boolean);
      } catch (e) {
        return [];
      }
    },
    onDragActive(active) {
      if (this.busy) return;
      this.dragActive = active;
    },
    openFileDialog() {
      if (this.busy) return;
      this.$refs.fileInput.click();
    },
    onDropzoneClick(e) {
      if (e.target.closest('button')) return;
      this.openFileDialog();
    },
    onDrop(e) {
      this.dragActive = false;
      if (this.busy) return;
      this.acceptFiles(e.dataTransfer && e.dataTransfer.files);
    },
    onFileInputChange(e) {
      this.acceptFiles(e.target.files);
      e.target.value = '';
    },
    acceptFiles(fileList) {
      if (this.busy) return;
      const files = Array.from(fileList || []);
      const existingNames = this.existingAttachmentNames();
      const skippedDup = [];
      const skippedExt = [];

      for (const file of files) {
        const ext = this.extractExtension(file.name).toLowerCase();
        // 許可拡張子以外は受け付けない
        if (!ALLOWED_ATTACHMENT_EXTENSIONS.includes(ext)) {
          skippedExt.push(file.name);
          continue;
        }
        const normalized = this.normalizeFileName(file.name);
        // 選択済み・登録済みと同名のファイルは重複として除外する
        const dupInList = this.items.some((it) => this.normalizeFileName(it.original_name) === normalized);
        const dupInExisting = existingNames.includes(normalized);
        if (dupInList || dupInExisting) {
          skippedDup.push(file.name);
          continue;
        }
        this.items.push({
          _uuid: this.generateUuid(),
          _file: file,
          filename: '',
          upload_expires_at: '',
          upload_token: '',
          filesize: file.size,
          original_name: file.name,
          extension: ext,
          status: 'pending',
          percent: 0,
          error: '',
        });
      }

      this.duplicateNotice = skippedDup.length > 0
        ? this.__('video.upload_duplicate_skipped').replace(':names', skippedDup.join(', '))
        : '';
      this.extensionNotice = skippedExt.length > 0
        ? this.__('video.upload_extension_rejected').replace(':names', skippedExt.join(', '))
        : '';
    },
    removeItem(idx) {
      if (this.busy) return;
      if (!this.items[idx] || this.items[idx].status !== 'pending') return;
      this.items.splice(idx, 1);
    },
    async uploadAll(mode) {
      if (this.busy) return;
      // 「失敗分と未送信分をアップロード」は失敗行と未送信行をまとめて送る。
      // 「アップロード」(mode: 'pending') は未送信行だけを送る現行のまま。
      const targets = mode === 'failed'
        ? this.items.filter((it) => it.status === 'failed' || it.status === 'pending')
        : this.items.filter((it) => it.status === mode);
      if (targets.length === 0) return;

      this.busy = true;
      this.uploadSessionTotal = targets.length;
      this.uploadSessionDone = 0;
      this.uploadSessionFailed = 0;
      try {
        // 同時 uploadConcurrency 件までワーカーを走らせ、1 件終わるごとに次を始める。
        // item 単位で catch しているため、1 件の失敗が他の送信を止めない。
        const queue = [...targets];
        const runWorker = async () => {
          let item = queue.shift();
          while (item) {
            item.status = 'uploading';
            item.percent = 0;
            item.error = '';
            try {
              await this.uploadOne(item);
              item.status = 'success';
              item.percent = 100;
              this.uploadSessionDone += 1;
            } catch (err) {
              item.status = 'failed';
              item.error = (err && err.message) ? err.message : this.__('video.upload_failed_generic');
              this.uploadSessionFailed += 1;
            }
            item = queue.shift();
          }
        };
        const workerCount = Math.min(this.uploadConcurrency, targets.length);
        await Promise.all(Array.from({ length: workerCount }, () => runWorker()));
      } finally {
        this.busy = false;
      }
    },
    async checkUploadAllowed(file) {
      if (!this.check_upload_endpoint) {
        throw new Error(this.__('video.upload_failed_generic'));
      }
      const csrfToken = document.querySelector('meta[name="csrf-token"]')?.getAttribute('content') || '';
      const res = await fetch(this.check_upload_endpoint, {
        method: 'POST',
        credentials: 'same-origin',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json',
          'X-Requested-With': 'XMLHttpRequest',
          'X-CSRF-TOKEN': csrfToken,
        },
        body: JSON.stringify({ filesize: file.size, original_name: file.name }),
      });
      if (!res.ok) {
        const errData = await res.json().catch(() => ({}));
        throw new Error(errData.message || this.__('video.upload_failed_generic'));
      }
      return res.json();
    },
    async uploadOne(item) {
      const file = item._file;
      if (!file) {
        throw new Error(this.__('video.upload_failed_generic'));
      }

      const prepared = await this.checkUploadAllowed(file);
      if (!prepared || !prepared.key) {
        throw new Error(this.__('video.upload_failed_generic'));
      }
      const objectKey = prepared.key;
      // ACL=private / Tagging=status=pending はサーバが署名に入れる
      await uploadWithPresignedUrls({
        file,
        endpoints: this.presign_endpoints,
        grant: prepared,
        contentType: file.type,
        partSize: 20 * 1024 * 1024,
        queueSize: 20,
        onProgress: (evt) => {
          item.percent = parseInt((evt.loaded * 100) / evt.total, 10);
        },
      });

      item.filename = objectKey;
      item.upload_expires_at = prepared.expires_at;
      item.upload_token = prepared.upload_token;
      item.filesize = file.size;
      item.original_name = file.name;
      item.extension = this.extractExtension(file.name);
    },
  },
};
</script>
